import { workspaceHref } from "@/cowork/workspace-route"
import {
  deleteArtifact,
  downloadPortfolioReport,
  exportPortfolioReportPdf,
  listPortfolioReports,
  ReportPdfExportError,
  type CoworkPortfolioReport,
} from "@/cowork/api"
import { collectionPath, useCollectionScope } from "@/cowork/scope-url"
import { A } from "@solidjs/router"
import { useCoworkWorkspace } from "@/cowork/workspace-context"
import { useLanguage } from "@/context/language"
import { dateFormatter } from "@/utils/date"
import { ReportShareDialog } from "@/components/cowork-report-share"
import { DialogCoworkDeleteReport } from "@/pages/home/cowork-project-dialogs"
import { ReportPreview, ReportTab } from "@/pages/session/v2/cowork-files-panel"
import { ReportPreviewLink } from "@/components/report-preview-link"
import { ReportHtmlPreview } from "@/components/report-html-preview"
import { reportDisplayTitle, reportFormatLabel } from "@/cowork/report-preview"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { MenuV2 } from "@opencode-ai/ui/v2/menu-v2"
import { FileIcon } from "@opencode-ai/ui/file-icon"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import { createEffect, createMemo, createResource, For, on, onCleanup, onMount, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { ReportAccountingReview } from "@/components/report-accounting-review"
import { Portal } from "solid-js/web"

type PreviewKind = "html" | "text" | "pdf" | "image" | "office" | "unsupported"

function missingReportOutput(report: CoworkPortfolioReport) {
  const value = report.artifact_metadata.output_delivery
  if (!value || typeof value !== "object" || Array.isArray(value)) return
  const delivery = value as { missing_formats?: unknown; reasons?: unknown }
  if (!Array.isArray(delivery.missing_formats)) return
  const missing = delivery.missing_formats.filter((format): format is string =>
    typeof format === "string" && /^[a-z0-9]{1,10}$/.test(format))
  if (!missing.length) return
  const reasons = delivery.reasons && typeof delivery.reasons === "object" && !Array.isArray(delivery.reasons)
    ? delivery.reasons as Record<string, unknown> : {}
  return { formats: missing.map((format) => format.toUpperCase()).join(", "),
    reason: missing.map((format) => reasons[format]).filter((reason): reason is string => typeof reason === "string").join(" ") }
}

function workingPaperReason(report: CoworkPortfolioReport) {
  const paper = report.artifact_metadata.working_paper
  return paper && typeof paper === "object" && !Array.isArray(paper) && typeof (paper as { reason?: unknown }).reason === "string"
    ? (paper as { reason: string }).reason : undefined
}

export function CoworkReportsPage() {
  const scope = useCollectionScope()
  const context = useCoworkWorkspace()
  const language = useLanguage()
  return <Show when={(!scope().companyID || context.state.entityId === scope().companyID) &&
    (!scope().projectID || context.selectedProject()?.id === scope().projectID)}
    fallback={<p role="status" class="p-7">{language.t(scope().companyID && context.state.entityId !== scope().companyID
      ? "cowork.enterprise.scope.selectCompany" : "cowork.enterprise.scope.selectProject")}</p>}>
    <CoworkReportsView
      entityID={scope().companyID || "ALL"}
      projectID={scope().projectID}
      periodID={scope().periodID}
    />
  </Show>
}

export function CoworkReportsView(props: {
  entityID: string
  projectID?: string
  periodID?: string
  embedded?: boolean
}) {
  const language = useLanguage()
  const dialog = useDialog()
  const templatesHref = () => workspaceHref(collectionPath("report-templates", {
    companyID: props.entityID === "ALL" ? undefined : props.entityID,
    projectID: props.projectID,
    periodID: props.periodID,
  }))
  const [state, setState] = createStore({
    workspace: false,
    mounted: true,
    search: "",
    status: "all",
    preview: undefined as CoworkPortfolioReport | undefined,
    previewKind: "unsupported" as PreviewKind,
    previewContent: "",
    previewUrl: "",
    previewLoading: false,
    previewError: "",
    deleting: "",
    exportingPdf: "",
    error: "",
  })
  onCleanup(() => setState("mounted", false))
  const scopeKey = () => `${props.entityID}:${props.projectID ?? ""}:${props.periodID ?? ""}`
  const [reports, reportActions] = createResource(scopeKey, () =>
    listPortfolioReports({ entityID: props.entityID, projectID: props.projectID, periodID: props.periodID }),
  )
  const refreshAfterEditing = () => void reportActions.refetch()
  onMount(() => window.addEventListener("focus", refreshAfterEditing))
  onCleanup(() => window.removeEventListener("focus", refreshAfterEditing))
  const filtered = createMemo(() => {
    if (reports.loading) return []
    const query = state.search.trim().toLowerCase()
    return (reports() ?? []).filter((report) => {
      if (state.status !== "all" && report.report_status !== state.status) return false
      if (!query) return true
      return `${report.title} ${report.company_name} ${report.project_name} ${report.format}`
        .toLowerCase()
        .includes(query)
    })
  })

  const download = async (report: CoworkPortfolioReport) => {
    const result = await downloadPortfolioReport(report.id)
    const url = URL.createObjectURL(result.blob)
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = result.filename
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const exportPdf = async (report: CoworkPortfolioReport) => {
    if (state.exportingPdf) return
    const scope = scopeKey()
    setState({ exportingPdf: report.id, error: "" })
    try {
      const result = await exportPortfolioReportPdf(report.id)
      const url = URL.createObjectURL(result.blob)
      const anchor = document.createElement("a")
      try {
        anchor.href = url
        anchor.download = result.filename
        document.body.appendChild(anchor)
        anchor.click()
      } finally {
        anchor.remove()
        URL.revokeObjectURL(url)
      }
    } catch (error) {
      if (scope === scopeKey()) {
        const status = error instanceof ReportPdfExportError ? error.status : 0
        setState(
          "error",
          language.t(
            status === 503
              ? "cowork.enterprise.reports.exportPdfUnavailable"
              : status === 429
                ? "cowork.enterprise.reports.exportPdfBusy"
                : status === 504
                  ? "cowork.enterprise.reports.exportPdfTimeout"
                  : "cowork.enterprise.reports.exportPdfError",
          ),
        )
      }
    } finally {
      setState("exportingPdf", "")
    }
  }

  const PdfExportButton = (props: { report: CoworkPortfolioReport }) => (
    <Show when={pdfExportableReport(props.report)}>
      <button type="button" class="report-workspace-action"
        disabled={!!state.exportingPdf}
        aria-busy={state.exportingPdf === props.report.id}
        aria-label={language.t(
          state.exportingPdf === props.report.id
            ? "cowork.enterprise.reports.exportingPdf"
            : "cowork.enterprise.reports.exportPdf",
        )}
        title={language.t(
          state.exportingPdf === props.report.id
            ? "cowork.enterprise.reports.exportingPdf"
            : "cowork.enterprise.reports.exportPdf",
        )}
        onClick={() => void exportPdf(props.report)}
      >{language.t(state.exportingPdf === props.report.id ? "cowork.enterprise.reports.exportingPdf" : "cowork.enterprise.reports.exportPdf")}</button>
    </Show>
  )

  const edit = (report: CoworkPortfolioReport) => {
    const query = new URLSearchParams({ companyID: report.company_id })
    window.open(
      workspaceHref(`/reports/${encodeURIComponent(report.project_id)}/${encodeURIComponent(report.id)}/edit?${query}`),
      "_blank",
      "noopener,noreferrer",
    )
  }

  const share = (report: CoworkPortfolioReport) => {
    closePreview()
    const scope = scopeKey()
    let open = true
    void dialog.show(
      () => <ReportShareDialog artifact={report} active={() => state.mounted && open && scope === scopeKey()} />,
      () => {
        open = false
      },
    )
  }
  const ShareButton = (props: { report: CoworkPortfolioReport }) => (
    <button type="button" class="report-workspace-action"
      aria-label={language.t("cowork.enterprise.reports.share")}
      title={language.t("cowork.enterprise.reports.share")}
      onClick={() => share(props.report)}
    >{language.t("cowork.enterprise.reports.share")}</button>
  )

  let previewRequest = 0
  const closePreview = () => {
    previewRequest++
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl)
    setState({ preview: undefined, previewUrl: "", previewContent: "", previewError: "", previewLoading: false })
  }
  onCleanup(closePreview)
  createEffect(on(scopeKey, closePreview, { defer: true }))

  const view = async (report: CoworkPortfolioReport) => {
    closePreview()
    const request = previewRequest
    if (officePreviewFormat(report.format)) {
      setState({ preview: report, previewKind: "office", previewLoading: false })
      return
    }
    if (report.preview_html) {
      setState({ preview: report, previewKind: "html", previewContent: report.preview_html })
      return
    }
    setState({ preview: report, previewLoading: true, previewKind: "unsupported" })
    try {
      const result = await downloadPortfolioReport(report.id)
      if (request !== previewRequest) return
      const format = report.format.toLowerCase()
      const mime = result.blob.type.toLowerCase()
      if (mime.includes("pdf") || format === "pdf") {
        setState({ previewKind: "pdf", previewUrl: URL.createObjectURL(result.blob) })
      } else if (mime.startsWith("image/") || /^(png|jpe?g|gif|webp|svg)$/.test(format)) {
        setState({ previewKind: "image", previewUrl: URL.createObjectURL(result.blob) })
      } else if (mime.includes("html") || format === "html") {
        const content = await result.blob.text()
        if (request !== previewRequest) return
        setState({ previewKind: "html", previewContent: content })
      } else if (mime.startsWith("text/") || /^(txt|csv|json|xml|md)$/.test(format)) {
        const content = await result.blob.text()
        if (request !== previewRequest) return
        setState({
          previewKind: "text",
          previewContent: format === "json" ? formatJson(content) : content,
        })
      }
    } catch (error) {
      if (request === previewRequest) setState("previewError", error instanceof Error ? error.message : String(error))
    } finally {
      if (request === previewRequest) setState("previewLoading", false)
    }
  }

  const remove = async (report: CoworkPortfolioReport) => {
    if (state.deleting) return
    setState({ deleting: report.id, error: "" })
    try {
      await deleteArtifact(report.project_id, report.id, report.company_id)
      if (state.preview?.id === report.id) closePreview()
      await reportActions.refetch()
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("deleting", "")
    }
  }

  return (
    <div classList={{ "min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10": !props.embedded }}>
      <div classList={{ "mx-auto max-w-[1500px]": !props.embedded }}>
        <Show when={!props.embedded}>
          <header>
            <h1 class="text-[30px] font-semibold tracking-[-0.025em]">{language.t("cowork.enterprise.nav.reports")}</h1>
            <p class="mt-1 text-[13px] text-v2-text-text-muted">
              {language.t("cowork.enterprise.section.reports")}
            </p>
          </header>
        </Show>

        <div class={props.embedded ? "" : "mt-7"}>
          <Show when={state.error}>
            <div class="mb-3 rounded-lg bg-v2-state-bg-danger px-4 py-3 text-[11px] text-v2-state-fg-danger">
              {state.error}
            </div>
          </Show>
          <div class="mb-3 flex flex-col gap-3 sm:flex-row sm:justify-between">
            <input
              class="h-10 min-w-[260px] rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none placeholder:text-v2-text-text-faint"
              aria-label={language.t("cowork.enterprise.reports.searchLabel")}
              placeholder={language.t("cowork.enterprise.reports.search")}
              value={state.search}
              onInput={(event) => setState("search", event.currentTarget.value)}
            />
            <div class="flex flex-wrap items-center gap-3">
              <select
                class="h-10 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none"
                aria-label={language.t("cowork.enterprise.reports.statusFilter")}
                value={state.status}
                onChange={(event) => setState("status", event.currentTarget.value)}
              >
                <option value="all">{language.t("cowork.enterprise.reports.allStatuses")}</option>
                <option value="draft">{language.t("cowork.enterprise.reports.status.draft")}</option>
                <option value="ready_for_review">{language.t("cowork.enterprise.reports.status.readyForReview")}</option>
                <option value="approved">{language.t("cowork.enterprise.reports.status.approved")}</option>
                <option value="blocked">{language.t("cowork.enterprise.reports.status.blocked")}</option>
              </select>
              <button type="button" class="report-workspace-action" aria-pressed={state.workspace} onClick={() => setState("workspace", (value) => !value)}>{language.t(state.workspace ? "cowork.report.workspace.list" : "cowork.report.workspace.view")}</button>
              <A href={templatesHref()} class="inline-flex h-10 items-center rounded-lg bg-v2-background-bg-contrast px-4 text-[12px] font-semibold text-v2-text-text-contrast hover:opacity-90">
                {language.t("cowork.enterprise.workflows.step.generate_report")}
              </A>
            </div>
          </div>
          <Show
            when={!reports.loading && filtered().length > 0}
            fallback={
              <ReportEmpty
                loading={reports.loading}
                title={language.t("cowork.enterprise.reports.emptyTitle")}
                description={language.t("cowork.enterprise.reports.emptyDescription")}
              />
            }
          >
            <Show when={state.workspace} fallback={<div class="divide-y divide-v2-border-border-base overflow-hidden rounded-xl border border-v2-border-border-base">
              <For each={filtered()}>
                {(report) => (
                  <article
                    class="group cursor-pointer bg-v2-background-bg-base px-4 py-4 transition-colors hover:bg-v2-overlay-simple-overlay-hover sm:px-5"
                    onClick={(event) => {
                      if (
                        (event.target as HTMLElement).closest("button, a, input, select, textarea, [role='menuitem']")
                      )
                        return
                      void view(report)
                    }}
                  >
                    <div class="flex flex-wrap items-center gap-3 sm:flex-nowrap sm:gap-4">
                      <ReportFormatIcon report={report} class="size-7 shrink-0" />
                      <div class="min-w-0 flex-1 basis-[calc(100%-3rem)] sm:basis-auto">
                        <h2 class="text-[14px] font-semibold leading-5">
                          <button type="button" class="max-w-full truncate text-left hover:underline" title={reportDisplayTitle(report.title)} onClick={() => void view(report)}>{reportDisplayTitle(report.title)}</button>
                        </h2>
                        <div class="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-v2-text-text-muted">
                          <span>{report.company_name}</span>
                          <span>{report.project_name}</span>
                          <Show when={report.period_name}><span>{report.period_name}</span></Show>
                          <span>{reportFormatLabel(report.format, language.t("cowork.pipeline.report"))}</span>
                          <span>{language.t("cowork.pipeline.records.metric.created")}: {formatDate(report.created_at)}</span>
                        </div>
                        <div class="mt-1 flex flex-wrap gap-2 text-[11px] text-v2-text-text-muted">
                          <Show when={missingReportOutput(report)}>{(missing) =>
                            <span role="status" class="rounded bg-v2-state-bg-warning px-2 py-1 text-v2-text-text-base">{language.t("cowork.enterprise.reports.missingOutput", { formats: missing().formats })}</span>
                          }</Show>
                          <Show when={workingPaperReason(report)}>
                            <span class="rounded bg-v2-state-bg-warning px-2 py-1 text-v2-text-text-base">{language.t("cowork.enterprise.reports.workingPaper")}</span>
                          </Show>
                        </div>
                      </div>
                      <div class="ml-auto flex shrink-0 items-center gap-2">
                        <ReportStatus status={report.report_status} />
                        <button type="button" class="report-workspace-action"
                          aria-label={language.t("cowork.enterprise.reports.download")}
                          title={language.t("cowork.enterprise.reports.download")}
                          onClick={() => void download(report)}
                        >{language.t("cowork.enterprise.reports.download")}</button>
                        <div onClick={(event) => event.stopPropagation()}>
                          <MenuV2 gutter={6} modal={false} placement="bottom-end">
                            <MenuV2.Trigger
                              as={IconButtonV2}
                              size="small"
                              variant="ghost-muted"
                              icon={<IconV2 name="outline-dots" size="small" />}
                              aria-label={language.t("common.moreOptions")}
                              title={language.t("common.moreOptions")}
                            />
                            <MenuV2.Portal>
                              <MenuV2.Content>
                                <Show when={spreadsheetReport(report)}>
                                  <MenuV2.Item onSelect={() => edit(report)}>
                                    <span class="flex items-center gap-2">
                                      <IconV2 name="edit" size="small" />
                                      {language.t("common.edit")}
                                    </span>
                                  </MenuV2.Item>
                                </Show>
                                <Show when={pdfExportableReport(report)}>
                                  <MenuV2.Item disabled={!!state.exportingPdf} onSelect={() => void exportPdf(report)}>
                                    <span class="flex items-center gap-2">
                                      <IconV2 name="file-pdf" size="small" />
                                      {language.t(
                                        state.exportingPdf === report.id
                                          ? "cowork.enterprise.reports.exportingPdf"
                                          : "cowork.enterprise.reports.exportPdf",
                                      )}
                                    </span>
                                  </MenuV2.Item>
                                </Show>
                                <MenuV2.Item onSelect={() => share(report)}>
                                  <span class="flex items-center gap-2">
                                    <IconV2 name="outline-share" size="small" />
                                    {language.t("cowork.enterprise.reports.share")}
                                  </span>
                                </MenuV2.Item>
                                <MenuV2.Separator />
                                <MenuV2.Item
                                  disabled={state.deleting === report.id}
                                  onSelect={() =>
                                    dialog.show(() => (
                                      <DialogCoworkDeleteReport
                                        name={report.title}
                                        onDelete={() => void remove(report)}
                                      />
                                    ))
                                  }
                                >
                                  <span class="flex items-center gap-2 text-v2-state-fg-danger">
                                    <IconV2 name="trash" size="small" />
                                    {language.t("common.delete")}
                                  </span>
                                </MenuV2.Item>
                              </MenuV2.Content>
                            </MenuV2.Portal>
                          </MenuV2>
                        </div>
                      </div>
                    </div>
                  </article>
                )}
              </For>
            </div>}>
              <div class="flex h-[75vh] min-h-[540px] flex-col">
                <ReportTab artifacts={filtered()} loading={reports.loading} onRefresh={() => void reportActions.refetch()}
                  onDelete={async (artifact) => { await deleteArtifact(artifact.project_id, artifact.id, artifact.company_id); await reportActions.refetch() }}
                  details={(artifact) => <Show when={filtered().find((item) => item.id === artifact.id)}>{(report) => <>
                    <Show when={missingReportOutput(report())}>{(missing) => <p role="status" class="bg-v2-state-bg-warning p-3 text-12-regular">{language.t("cowork.enterprise.reports.missingOutput", { formats: missing().formats })} {missing().reason}</p>}</Show>
                    <Show when={workingPaperReason(report())}>{(reason) => <p role="status" class="bg-v2-state-bg-warning p-3 text-12-regular">{language.t("cowork.enterprise.reports.workingPaper")} {reason()}</p>}</Show>
                    <ReportAccountingReview report={report()} />
                  </>}</Show>} />
              </div>
            </Show>
          </Show>
        </div>
      </div>
      <Show when={state.preview}>
        {(report) => (
          <Portal>
            <div class="fixed inset-0 z-[110] flex bg-v2-overlay-simple-overlay-scrim" onClick={closePreview}>
              <section
                class="flex h-dvh w-full flex-col overflow-hidden bg-v2-background-bg-base"
                onClick={(event) => event.stopPropagation()}
              >
                <header class="flex shrink-0 items-center justify-between gap-4 border-b border-v2-border-border-base px-5 py-4">
                  <div class="flex min-w-0 items-center gap-3">
                    <FileIcon
                      node={{ path: reportIconPath(report()), type: "file" }}
                      class="size-7 shrink-0"
                      role="img"
                      aria-label={reportFormatLabel(report().format, language.t("cowork.pipeline.report"))}
                    />
                    <h2 class="truncate text-[15px] font-semibold">{reportDisplayTitle(report().title)}</h2>
                  </div>
                  <div class="flex flex-wrap items-center gap-2">
                    <ReportPreviewLink projectID={report().project_id} artifactID={report().id} companyID={report().company_id} />
                    <Show when={spreadsheetReport(report())}>
                      <button
                        type="button"
                        class="report-workspace-action"
                        aria-label={language.t("common.edit")}
                        title={language.t("common.edit")}
                        onClick={() => edit(report())}
                      >{language.t("common.edit")}</button>
                    </Show>
                    <button type="button" class="report-workspace-action"
                      aria-label={language.t("cowork.enterprise.reports.download")}
                      title={language.t("cowork.enterprise.reports.download")}
                      onClick={() => void download(report())}
                    >{language.t("cowork.enterprise.reports.download")}</button>
                    <PdfExportButton report={report()} />
                    <ShareButton report={report()} />
                    <button
                      type="button"
                      class="report-workspace-action"
                      aria-label={language.t("cowork.enterprise.reports.closePreview")}
                      title={language.t("cowork.enterprise.reports.closePreview")}
                      onClick={closePreview}
                    >{language.t("common.close")}</button>
                  </div>
                </header>
                <Show when={missingReportOutput(report())}>{(missing) =>
                  <div role="status" class="shrink-0 bg-v2-state-bg-warning px-5 py-3 text-[12px] text-v2-text-text-base">
                    <p class="font-semibold">{language.t("cowork.enterprise.reports.missingOutput", { formats: missing().formats })}</p>
                    <Show when={missing().reason}><p class="mt-1">{missing().reason}</p></Show>
                  </div>
                }</Show>
                <Show when={workingPaperReason(report())}>{(reason) =>
                  <div role="status" class="shrink-0 bg-v2-state-bg-warning px-5 py-3 text-[12px] text-v2-text-text-base">
                    <p class="font-semibold">{language.t("cowork.enterprise.reports.workingPaper")}</p>
                    <p class="mt-1">{reason()}</p>
                  </div>
                }</Show>
                <Show when={state.error}>
                  <div role="alert" class="bg-v2-state-bg-danger px-5 py-3 text-[11px] text-v2-state-fg-danger">
                    {state.error}
                  </div>
                </Show>
                <div class="min-h-0 flex-1 overflow-auto bg-v2-background-bg-layer-02 p-4">
                  <ReportAccountingReview report={report()} />
                  <Show
                    when={!state.previewLoading}
                    fallback={
                      <div class="grid h-full place-items-center text-[12px] text-v2-text-text-muted">
                        {language.t("cowork.enterprise.reports.loadingPreview")}
                      </div>
                    }
                  >
                    <Show when={!state.previewError} fallback={<PreviewFallback text={state.previewError} />}>
                      <Show when={state.previewKind === "html"}>
                        <ReportHtmlPreview srcdoc={state.previewContent} title={report().title} />
                      </Show>
                      <Show when={state.previewKind === "pdf"}>
                        <iframe
                          class="h-full min-h-[600px] w-full rounded-lg border border-v2-border-border-base bg-white"
                          src={state.previewUrl}
                          title={language.t("cowork.enterprise.reports.previewTitle", { report: report().title })}
                        />
                      </Show>
                      <Show when={state.previewKind === "image"}>
                        <div class="flex min-h-full items-center justify-center">
                          <img src={state.previewUrl} alt={report().title} class="max-h-full max-w-full rounded-lg" />
                        </div>
                      </Show>
                      <Show when={state.previewKind === "text"}>
                        <pre class="min-h-full whitespace-pre-wrap rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-5 text-[12px] leading-5">
                          {state.previewContent}
                        </pre>
                      </Show>
                      <Show when={state.previewKind === "office"}>
                        <ReportPreview artifact={report()} load={() => downloadPortfolioReport(report().id)} />
                      </Show>
                      <Show when={state.previewKind === "unsupported"}>
                        <PreviewFallback text={language.t("cowork.enterprise.reports.previewUnavailable")} />
                      </Show>
                    </Show>
                  </Show>
                </div>
              </section>
            </div>
          </Portal>
        )}
      </Show>
    </div>
  )
}

function PreviewFallback(props: { text: string }) {
  return (
    <div class="grid h-full min-h-72 place-items-center text-center text-[12px] text-v2-text-text-muted">
      {props.text}
    </div>
  )
}

function formatJson(value: string) {
  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch {
    return value
  }
}

function officePreviewFormat(format: string) {
  return (
    /^(xlsx|xlsm|excel|spreadsheet|workbook|pptx|slides|presentation|powerpoint|docx|word)$/i.test(
      format.replace(/^\./, ""),
    ) || format.toLowerCase() === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  )
}

function spreadsheetReport(report: CoworkPortfolioReport) {
  return /^(xlsx|xlsm|excel|spreadsheet|workbook)$/i.test(report.format.replace(/^\./, ""))
}

function pdfExportableReport(report: CoworkPortfolioReport) {
  return /^(xlsx|excel|spreadsheet|workbook|html|htm|text\/html)$/i.test(report.format.replace(/^\./, ""))
}

function ReportStatus(props: { status: CoworkPortfolioReport["report_status"] }) {
  const language = useLanguage()
  return (
    <span
      class="rounded-md px-2 py-1 text-[10px] font-medium"
      classList={{
        "bg-v2-background-bg-layer-03 text-v2-text-text-muted": props.status === "draft",
        "bg-v2-state-bg-warning text-v2-state-fg-warning": props.status === "ready_for_review",
        "bg-v2-state-bg-success text-v2-state-fg-success": props.status === "approved",
        "bg-v2-state-bg-danger text-v2-state-fg-danger": props.status === "blocked",
      }}
    >
      {reportStatusLabel(props.status, language)}
    </span>
  )
}

function ReportFormatIcon(props: { report: CoworkPortfolioReport; class?: string }) {
  const language = useLanguage()
  if (spreadsheetReport(props.report)) {
    return (
      <svg viewBox="0 0 32 32" class={props.class} role="img" aria-label={props.report.format.toUpperCase()}>
        <path fill="#107C41" d="M12 3h15a2 2 0 0 1 2 2v22a2 2 0 0 1-2 2H12z" />
        <path fill="#33C481" d="M17 7h9v4h-9z" />
        <path fill="#21A366" d="M17 13h9v4h-9zM17 19h9v4h-9z" />
        <path
          fill="#185C37"
          d="M4.5 7h14A1.5 1.5 0 0 1 20 8.5v15a1.5 1.5 0 0 1-1.5 1.5h-14A1.5 1.5 0 0 1 3 23.5v-15A1.5 1.5 0 0 1 4.5 7"
        />
        <path fill="#fff" d="m7 11 3 5-3.2 5h2.8l1.9-3.3 1.9 3.3h2.8L13 15.9l3-4.9h-2.7l-1.7 3-1.7-3z" />
      </svg>
    )
  }
  if (/^(html|htm|text\/html)$/i.test(props.report.format.replace(/^\./, ""))) {
    return (
      <svg viewBox="0 0 32 32" class={props.class} role="img" aria-label={language.t("cowork.pipeline.report")}>
        <rect width="28" height="24" x="2" y="4" rx="3" fill="#5B5BD6" />
        <path fill="#fff" fill-opacity=".2" d="M2 7a3 3 0 0 1 3-3h22a3 3 0 0 1 3 3v3H2z" />
        <circle cx="6" cy="7" r="1" fill="#fff" />
        <circle cx="9" cy="7" r="1" fill="#fff" fill-opacity=".7" />
        <path fill="#fff" fill-opacity=".9" d="M6 14h8v4H6zM17 14h9v2h-9zM17 18h9v6h-9zM6 20h3v4H6zM11 19h3v5h-3z" />
      </svg>
    )
  }
  return (
    <FileIcon
      node={{ path: reportIconPath(props.report), type: "file" }}
      class={props.class}
      role="img"
      aria-label={props.report.format.toUpperCase()}
    />
  )
}

function reportIconPath(report: CoworkPortfolioReport) {
  const format = report.format.replace(/^\./, "").toLowerCase()
  const extension =
    format === "excel" || format === "spreadsheet" || format === "workbook"
      ? "xlsx"
      : format === "word"
        ? "docx"
        : format === "slides" || format === "presentation" || format === "powerpoint"
          ? "pptx"
          : format === "text/html"
            ? "html"
            : format
  return `${report.title}.${extension}`
}

function ReportEmpty(props: { title: string; description: string; loading?: boolean }) {
  return (
    <div class="grid min-h-52 place-items-center rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-8 text-center">
      <Show
        when={!props.loading}
        fallback={
          <span class="size-5 animate-spin rounded-full border-2 border-v2-border-border-base border-t-v2-icon-icon-base" />
        }
      >
        <div>
          <h3 class="text-[14px] font-semibold">{props.title}</h3>
          <p class="mx-auto mt-1 max-w-md text-[11px] leading-5 text-v2-text-text-muted">{props.description}</p>
        </div>
      </Show>
    </div>
  )
}

function formatDate(value: string) {
  const date = new Date(value)
  return Number.isFinite(date.getTime())
    ? dateFormatter(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date)
    : ""
}

function reportStatusLabel(status: CoworkPortfolioReport["report_status"], language: ReturnType<typeof useLanguage>) {
  const keys: Record<CoworkPortfolioReport["report_status"], keyof typeof import("@/i18n/en").dict> = {
    draft: "cowork.enterprise.reports.status.draft",
    ready_for_review: "cowork.enterprise.reports.status.readyForReview",
    approved: "cowork.enterprise.reports.status.approved",
    blocked: "cowork.enterprise.reports.status.blocked",
  }
  return language.t(keys[status])
}
